import { usePageTitle } from "../hooks";

/**
 * TermsOfServicePage - Terms of Service for JumpBean
 *
 * Covers usage terms for the educational platform targeting preschool children.
 * Accessible to both authenticated and unauthenticated users.
 */
export function TermsOfServicePage() {
  usePageTitle("Terms of Service");

  const lastUpdated = "January 5, 2026";

  return (
    <div className="flex flex-col">
      {/* Header Section */}
      <section className="relative py-16 md:py-20 bg-gradient-to-b from-magic-100 via-sunshine-50 to-white">
        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <h1 className="font-display font-extrabold text-display-lg md:text-display-xl text-neutral-900 mb-4 text-center">
            Terms of Service
          </h1>
          <p className="font-body text-body-lg text-neutral-600 text-center">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12">
              <p className="font-body text-body-lg text-neutral-700 leading-relaxed">
                Welcome to JumpBean! These Terms of Service ("Terms") govern your use of the
                JumpBean application, website, and services (collectively, the "Service") operated
                by JumpBean ("we," "us," or "our").
              </p>
              <p className="font-body text-body-lg text-neutral-700 leading-relaxed mt-4">
                By creating an account or using our Service, you agree to be bound by these Terms.
                If you do not agree to these Terms, please do not use our Service.
              </p>
              <p className="font-body text-body-lg text-neutral-700 leading-relaxed mt-4">
                <strong>Important:</strong> JumpBean is designed for preschool children (ages 4-5).
                Only parents or legal guardians may create accounts. Please also review our{" "}
                <a href="/privacy" className="text-magic-600 hover:text-magic-700 underline">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a href="/coppa" className="text-magic-600 hover:text-magic-700 underline">
                  COPPA Compliance
                </a>{" "}
                page.
              </p>
            </div>

            {/* Section 1 */}
            <TermsSection title="1. Eligibility and Account Registration">
              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Age Requirements
              </h4>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>
                  You must be at least 18 years old (or the age of majority in your jurisdiction)
                  to create an account.
                </li>
                <li>
                  Accounts may only be created by parents or legal guardians of children who will
                  use the Service.
                </li>
                <li>
                  Children cannot create their own accounts or provide personal information
                  directly.
                </li>
              </ul>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Account Responsibilities
              </h4>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>You are responsible for maintaining the security of your account credentials.</li>
                <li>You are responsible for all activities that occur under your account.</li>
                <li>
                  You must provide accurate and complete information when creating an account.
                </li>
                <li>
                  You must notify us immediately if you become aware of any unauthorized use of
                  your account.
                </li>
              </ul>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Parental Responsibility
              </h4>
              <p className="font-body text-body-md text-neutral-700">
                As a parent or guardian, you are responsible for supervising your child's use of
                the Service and ensuring their interactions are appropriate. You consent to the
                collection and use of information as described in our Privacy Policy on behalf of
                your child.
              </p>
            </TermsSection>

            {/* Section 2 */}
            <TermsSection title="2. Service Description">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                JumpBean is an educational application designed for preschool children that
                provides learning activities in Math, Spelling, Patterns, Typing, and Programming
                through gamified challenges with character guides.
              </p>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Service Features
              </h4>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>Educational challenges and activities across five learning disciplines</li>
                <li>Progress tracking and achievement systems</li>
                <li>Child profile management</li>
                <li>Parent dashboard for monitoring learning progress (paid tiers)</li>
              </ul>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Platform Availability
              </h4>
              <p className="font-body text-body-md text-neutral-700">
                The Service is available via web browser and mobile applications. We strive to
                maintain Service availability but do not guarantee uninterrupted access. We may
                modify, suspend, or discontinue any aspect of the Service at any time.
              </p>
            </TermsSection>

            {/* Section 3 */}
            <TermsSection title="3. Subscription Plans and Payments">
              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Subscription Tiers
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                JumpBean offers the following subscription options:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700 mb-4">
                <li>
                  <strong>Free (JumpBean Starter):</strong> Access to Level 1 of all disciplines,
                  1 child profile, basic progress tracking
                </li>
                <li>
                  <strong>Family ($5/month or $48/year):</strong> All levels in all disciplines,
                  up to 4 child profiles, parent dashboard with reports
                </li>
                <li>
                  <strong>Classroom ($29/month or $290/year):</strong> All Family features plus
                  up to 35 student profiles, class management tools, and exportable reports
                </li>
              </ul>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Payment Processing
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                All payments are processed through Stripe, a PCI DSS Level 1 certified payment
                processor. By subscribing to a paid plan, you authorize us to charge the payment
                method you provide for the subscription fees.
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>
                  Subscriptions automatically renew at the end of each billing period unless
                  canceled.
                </li>
                <li>You may cancel your subscription at any time through your account settings.</li>
                <li>
                  Cancellation takes effect at the end of the current billing period; you retain
                  access until then.
                </li>
                <li>
                  We do not store your full credit card details; all payment information is
                  securely handled by Stripe.
                </li>
              </ul>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Price Changes
              </h4>
              <p className="font-body text-body-md text-neutral-700">
                We may change subscription prices with at least 30 days' notice. Price changes
                will take effect at the start of your next billing period. Continued use of the
                Service after a price change constitutes acceptance of the new price.
              </p>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Refunds
              </h4>
              <p className="font-body text-body-md text-neutral-700">
                Subscription fees are generally non-refundable. However, if you are unsatisfied
                with the Service, please contact us at{" "}
                <a
                  href="mailto:support@jumpbean.app"
                  className="text-magic-600 hover:text-magic-700 underline"
                >
                  support@jumpbean.app
                </a>{" "}
                within 30 days of your initial subscription, and we will work with you to resolve
                any issues or provide a refund at our discretion.
              </p>
            </TermsSection>

            {/* Section 4 */}
            <TermsSection title="4. Acceptable Use">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                You agree to use the Service only for lawful purposes and in accordance with these
                Terms. You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>
                  Use the Service in any way that violates any applicable law or regulation
                </li>
                <li>
                  Attempt to gain unauthorized access to any part of the Service, other accounts,
                  or computer systems
                </li>
                <li>
                  Interfere with or disrupt the Service or servers/networks connected to the
                  Service
                </li>
                <li>Use any automated means to access the Service or collect information</li>
                <li>
                  Impersonate any person or entity or misrepresent your affiliation with any
                  person or entity
                </li>
                <li>
                  Share your account credentials with others or allow others to access your
                  account
                </li>
                <li>
                  Use the Service to harm, abuse, harass, or exploit children in any way
                </li>
              </ul>
            </TermsSection>

            {/* Section 5 */}
            <TermsSection title="5. Intellectual Property">
              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Our Content
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                The Service and its original content, features, functionality, characters,
                artwork, and educational materials are owned by JumpBean and are protected by
                copyright, trademark, and other intellectual property laws. This includes but is
                not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>
                  Character guides (Luna, Petal, Prism, Whiskers, Pixel) and associated artwork
                </li>
                <li>Educational challenges and game mechanics</li>
                <li>User interface designs and visual elements</li>
                <li>Audio content and sound effects</li>
                <li>Written content, including instructions and educational materials</li>
              </ul>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                License to Use
              </h4>
              <p className="font-body text-body-md text-neutral-700">
                We grant you a limited, non-exclusive, non-transferable, revocable license to
                access and use the Service for personal, non-commercial educational purposes in
                accordance with these Terms. This license does not include the right to copy,
                modify, distribute, sell, or lease any part of the Service.
              </p>
            </TermsSection>

            {/* Section 6 */}
            <TermsSection title="6. User Content and Data">
              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Your Data
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                You retain ownership of any data you provide to the Service, including child
                profile information and learning progress data. By using the Service, you grant
                us a license to use this data as described in our{" "}
                <a href="/privacy" className="text-magic-600 hover:text-magic-700 underline">
                  Privacy Policy
                </a>
                .
              </p>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Data Portability and Deletion
              </h4>
              <p className="font-body text-body-md text-neutral-700">
                You may request a copy of your data or deletion of your account and associated
                data at any time by contacting us. When you delete a child profile, all associated
                learning progress data is permanently deleted.
              </p>
            </TermsSection>

            {/* Section 7 */}
            <TermsSection title="7. Disclaimers">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p className="font-body text-body-md text-neutral-700 mb-4">We do not warrant that:</p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>The Service will meet your specific requirements or expectations</li>
                <li>The Service will be uninterrupted, timely, secure, or error-free</li>
                <li>Any errors in the Service will be corrected</li>
                <li>
                  The Service will produce any specific educational outcomes for your child
                </li>
              </ul>
              <p className="font-body text-body-md text-neutral-700 mt-4">
                JumpBean is designed to supplement, not replace, formal education and parental
                involvement in your child's learning.
              </p>
            </TermsSection>

            {/* Section 8 */}
            <TermsSection title="8. Limitation of Liability">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL JUMPBEAN, ITS
                DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR
                ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING
                WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE
                LOSSES, RESULTING FROM:
              </p>
              <ul className="list-disc list-inside space-y-2 font-body text-body-md text-neutral-700">
                <li>Your access to or use of (or inability to access or use) the Service</li>
                <li>Any conduct or content of any third party on the Service</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              </ul>
              <p className="font-body text-body-md text-neutral-700 mt-4">
                Our total liability for any claims arising out of or relating to these Terms or
                the Service shall not exceed the amount you paid us in the twelve (12) months
                prior to the claim.
              </p>
            </TermsSection>

            {/* Section 9 */}
            <TermsSection title="9. Indemnification">
              <p className="font-body text-body-md text-neutral-700">
                You agree to indemnify, defend, and hold harmless JumpBean and its officers,
                directors, employees, agents, and affiliates from and against any and all claims,
                damages, obligations, losses, liabilities, costs, and expenses arising from: (a)
                your use of the Service; (b) your violation of these Terms; (c) your violation of
                any third-party right, including any intellectual property or privacy right; or
                (d) any claim that your use of the Service caused damage to a third party.
              </p>
            </TermsSection>

            {/* Section 10 */}
            <TermsSection title="10. Account Termination">
              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Termination by You
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                You may terminate your account at any time by contacting us or using the account
                deletion feature in your settings. Upon termination, your right to use the Service
                will immediately cease.
              </p>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Termination by Us
              </h4>
              <p className="font-body text-body-md text-neutral-700 mb-4">
                We may terminate or suspend your account immediately, without prior notice or
                liability, for any reason, including if you breach these Terms. Upon termination,
                your right to use the Service will immediately cease.
              </p>

              <h4 className="font-display font-bold text-body-lg text-neutral-900 mt-6 mb-3">
                Effect of Termination
              </h4>
              <p className="font-body text-body-md text-neutral-700">
                Upon termination, all licenses and rights granted to you under these Terms will
                immediately terminate. We may delete your account data in accordance with our
                Privacy Policy. Sections that by their nature should survive termination will
                survive, including intellectual property provisions, disclaimers, limitations of
                liability, and indemnification.
              </p>
            </TermsSection>

            {/* Section 11 */}
            <TermsSection title="11. Changes to Terms">
              <p className="font-body text-body-md text-neutral-700">
                We reserve the right to modify these Terms at any time. If we make material
                changes, we will notify you by email and/or by posting a notice on the Service
                prior to the changes taking effect. Your continued use of the Service after any
                changes constitutes acceptance of the new Terms. We encourage you to review these
                Terms periodically.
              </p>
            </TermsSection>

            {/* Section 12 */}
            <TermsSection title="12. Governing Law and Disputes">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the
                State of Delaware, United States, without regard to its conflict of law
                provisions.
              </p>
              <p className="font-body text-body-md text-neutral-700">
                Any disputes arising out of or relating to these Terms or the Service shall first
                be attempted to be resolved through good-faith negotiation. If the dispute cannot
                be resolved through negotiation within 30 days, either party may pursue resolution
                through binding arbitration or, where permitted, in the courts of Delaware.
              </p>
            </TermsSection>

            {/* Section 13 */}
            <TermsSection title="13. General Provisions">
              <ul className="list-disc list-inside space-y-3 font-body text-body-md text-neutral-700">
                <li>
                  <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy
                  and COPPA Compliance documentation, constitute the entire agreement between you
                  and JumpBean regarding the Service.
                </li>
                <li>
                  <strong>Severability:</strong> If any provision of these Terms is found to be
                  unenforceable, the remaining provisions will continue in full force and effect.
                </li>
                <li>
                  <strong>Waiver:</strong> Our failure to enforce any right or provision of these
                  Terms will not be considered a waiver of those rights.
                </li>
                <li>
                  <strong>Assignment:</strong> You may not assign or transfer these Terms without
                  our prior written consent. We may assign these Terms without restriction.
                </li>
                <li>
                  <strong>No Third-Party Beneficiaries:</strong> These Terms do not create any
                  third-party beneficiary rights.
                </li>
              </ul>
            </TermsSection>

            {/* Section 14 */}
            <TermsSection title="14. Contact Us">
              <p className="font-body text-body-md text-neutral-700 mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-neutral-50 rounded-2xl p-6 border border-neutral-200">
                <p className="font-body text-body-md text-neutral-700">
                  <strong>JumpBean Support</strong>
                  <br />
                  Email:{" "}
                  <a
                    href="mailto:support@jumpbean.app"
                    className="text-magic-600 hover:text-magic-700 underline"
                  >
                    support@jumpbean.app
                  </a>
                </p>
              </div>
              <p className="font-body text-body-md text-neutral-700 mt-4">
                For privacy-related inquiries, please contact{" "}
                <a
                  href="mailto:privacy@jumpbean.app"
                  className="text-magic-600 hover:text-magic-700 underline"
                >
                  privacy@jumpbean.app
                </a>
                .
              </p>
            </TermsSection>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Reusable section component for terms content */
function TermsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10 pb-10 border-b border-neutral-200 last:border-b-0">
      <h2 className="font-display font-bold text-display-sm text-neutral-900 mb-4">{title}</h2>
      {children}
    </div>
  );
}

export default TermsOfServicePage;
